import { useState } from "react";
import api, { API } from "../../lib/api";
import { PageHeader } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Sparkles,
  Copy,
  Printer,
  Save,
  Loader2,
  Plus,
  Trash2,
  HeartPulse,
} from "lucide-react";

const GENERATORS = [
  {
    kind: "proceeding",
    label: "Meeting Proceeding",
    fields: ["Meeting type", "Date", "Venue", "Chairperson", "Attendees"],
  },
  {
    kind: "agenda",
    label: "Meeting Agenda",
    fields: ["Meeting type", "Date", "Key topics"],
  },
  {
    kind: "notice",
    label: "Notice",
    fields: ["Notice type", "Date", "Subject"],
  },
  {
    kind: "letter",
    label: "Letter",
    fields: ["Letter type", "Recipient", "Subject"],
  },
  { kind: "circular", label: "Circular / WhatsApp", fields: ["Subject"] },
  { kind: "formatter", label: "Document Formatter", fields: [] },
];

const HC_CATEGORIES = [
  "CCTV",
  "Security",
  "Electrical",
  "Pumps",
  "Waterproofing",
  "Fire",
  "STP",
  "Solar",
  "Lift",
  "Plumbing",
  "Painting",
  "Housekeeping",
  "Garden",
  "Common Areas",
];

export default function Tools() {
  return (
    <div>
      <PageHeader
        title="Free Trial Tools"
        subtitle="AI-assisted generators, health check and quotation comparison throughout your 60-day Society ERP trial."
      />
      <div
        className="bg-secondary/10 border border-secondary/30 text-secondary rounded-lg px-4 py-2.5 text-sm mb-6 inline-flex items-center gap-2"
        data-testid="tools-trial-note"
      >
        <Sparkles className="w-4 h-4" /> Included in your 60-day Society ERP
        trial
      </div>
      <Tabs defaultValue="generator">
        <TabsList className="mb-6">
          <TabsTrigger value="generator" data-testid="tab-generator">
            AI Generators
          </TabsTrigger>
          <TabsTrigger value="healthcheck" data-testid="tab-healthcheck">
            Health Check
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">
            Quotation Comparison
          </TabsTrigger>
        </TabsList>
        <TabsContent value="generator">
          <Generator />
        </TabsContent>
        <TabsContent value="healthcheck">
          <HealthCheck />
        </TabsContent>
        <TabsContent value="quotes">
          <QuoteCompare />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Generator() {
  const [kind, setKind] = useState("proceeding");
  const [fields, setFields] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [provider, setProvider] = useState("openai");
  const gen = GENERATORS.find((g) => g.kind === kind);

  const generate = async () => {
    setLoading(true);
    setResult({
      content: "",
      disclaimer: "AI-generated draft — review and edit before official use.",
    });
    try {
      const response = await fetch(`${API}/tools/generate/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          provider,
          session_id: `generator-${crypto.randomUUID()}`,
          fields: { ...fields, "Rough notes / content": notes },
        }),
      });
      if (!response.ok || !response.body)
        throw new Error("Could not start generation");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let content = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        events.forEach((event) => {
          const line = event
            .split("\n")
            .find((item) => item.startsWith("data: "));
          if (!line) return;
          const payload = JSON.parse(line.slice(6));
          if (payload.type === "token") content += payload.content;
          if (payload.type === "error") throw new Error(payload.detail);
        });
        setResult({
          content,
          disclaimer:
            "AI-generated draft — review and edit before official use.",
        });
      }
    } catch (error) {
      toast.error(error.message || "Generation failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    await fetch(`${API}/tools/documents`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: `${gen.label} - ${new Date().toLocaleDateString()}`,
        category: gen.label,
        content: result.content,
      }),
    });
    toast.success("Saved to Society Documents");
  };
  const copy = () => {
    navigator.clipboard.writeText(result.content);
    toast.success("Copied");
  };
  const letterheadPdf = async () => {
    try {
      const response = await fetch(`${API}/pdf/letter`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: gen.label.toUpperCase(),
          content: result.content,
        }),
      });
      if (!response.ok) throw new Error("Could not create letterhead PDF");
      window.open(URL.createObjectURL(await response.blob()), "_blank");
    } catch (error) {
      toast.error(error.message || "Could not generate PDF");
    }
  };
  const print = () => letterheadPdf();

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-card border rounded-xl p-5 space-y-4">
        <div>
          <Label className="mb-1.5 block text-sm">Tool</Label>
          <Select
            value={kind}
            onValueChange={(v) => {
              setKind(v);
              setResult(null);
              setFields({});
            }}
          >
            <SelectTrigger data-testid="gen-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GENERATORS.map((g) => (
                <SelectItem key={g.kind} value={g.kind}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1.5 block text-sm">AI provider</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="gen-provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">ChatGPT · GPT-5.4</SelectItem>
              <SelectItem value="gemini">Gemini · 3.1 Pro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {gen.fields.map((f) => (
          <div key={f}>
            <Label className="mb-1.5 block text-sm">{f}</Label>
            <Input
              value={fields[f] || ""}
              onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
            />
          </div>
        ))}
        <div>
          <Label className="mb-1.5 block text-sm">Rough notes / content</Label>
          <Textarea
            rows={7}
            data-testid="gen-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Type rough points; AI will structure them professionally..."
          />
        </div>
        <Button
          onClick={generate}
          disabled={loading}
          className="rounded-full gap-2 w-full"
          data-testid="gen-submit"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}{" "}
          Generate
        </Button>
      </div>

      <div className="bg-card border rounded-xl p-5">
        {!result ? (
          <div className="text-center text-muted-foreground py-24">
            <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40" />
            Generated document will appear here.
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-3 no-print flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={copy}
                className="gap-1.5"
                data-testid="gen-copy"
              >
                <Copy className="w-3.5 h-3.5" /> Copy
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={print}
                className="gap-1.5"
                data-testid="gen-print-letterhead"
              >
                <Printer className="w-3.5 h-3.5" /> Preview & Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={letterheadPdf}
                className="gap-1.5"
                data-testid="gen-letterhead-pdf"
              >
                <Printer className="w-3.5 h-3.5" /> Letterhead PDF
              </Button>
              <Button
                size="sm"
                onClick={save}
                className="gap-1.5"
                data-testid="gen-save"
              >
                <Save className="w-3.5 h-3.5" /> Save
              </Button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800 mb-3">
              {result.disclaimer}
            </div>
            <Textarea
              rows={20}
              value={result.content}
              onChange={(e) =>
                setResult({ ...result, content: e.target.value })
              }
              className="font-mono text-sm"
              data-testid="gen-result"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function HealthCheck() {
  const [answers, setAnswers] = useState({});
  const [report, setReport] = useState(null);
  const [requestInspection, setRequestInspection] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length === 0) {
      toast.error("Please assess at least one category");
      return;
    }
    const r = await api.post("/tools/health-check", {
      answers,
      request_inspection: requestInspection,
    });
    setReport(r.data);
    toast.success("Health check report generated");
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-card border rounded-xl p-5">
        <h3 className="font-display font-bold mb-4">
          Assess your society systems
        </h3>
        <div className="space-y-2.5">
          {HC_CATEGORIES.map((c) => (
            <div key={c} className="flex items-center justify-between gap-3">
              <span className="text-sm">{c}</span>
              <Select
                value={answers[c] || ""}
                onValueChange={(v) => setAnswers({ ...answers, [c]: v })}
              >
                <SelectTrigger className="w-40 h-9" data-testid={`hc-${c}`}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="attention">Attention</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={requestInspection}
            onChange={(e) => setRequestInspection(e.target.checked)}
            data-testid="hc-inspection"
          />
          Request a MySocietyMitra inspection
        </label>
        <Button
          onClick={submit}
          className="rounded-full gap-2 w-full mt-4"
          data-testid="hc-generate"
        >
          <HeartPulse className="w-4 h-4" /> Generate Report
        </Button>
      </div>
      <div className="bg-card border rounded-xl p-5">
        {!report ? (
          <div className="text-center text-muted-foreground py-24">
            <HeartPulse className="w-8 h-8 mx-auto mb-3 opacity-40" />
            Your report will appear here.
          </div>
        ) : (
          <div>
            <div className="text-center mb-4">
              <div className="font-display text-4xl font-black text-secondary">
                {report.score}%
              </div>
              <div className="text-sm text-muted-foreground">Health Score</div>
            </div>
            {[
              ["Critical", report.critical, "rose"],
              ["Attention Required", report.attention, "amber"],
              ["Upcoming", report.upcoming, "primary"],
              ["Good", report.good, "secondary"],
            ].map(([t, arr, tone]) => (
              <div key={t} className="mb-3">
                <div
                  className={`text-sm font-semibold text-${tone === "rose" ? "rose-600" : tone === "amber" ? "amber-600" : tone === "primary" ? "primary" : "secondary"}`}
                >
                  {t} ({arr.length})
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {arr.map((c) => (
                    <span
                      key={c}
                      className="text-xs bg-muted rounded-full px-2 py-0.5"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {report.inspection_requested && (
              <div className="mt-3 bg-secondary/10 text-secondary text-sm rounded-lg p-3">
                Inspection request submitted — our team will contact you.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuoteCompare() {
  const [title, setTitle] = useState("");
  const [quotes, setQuotes] = useState([
    {
      vendor: "",
      price: "",
      scope: "",
      material: "",
      warranty: "",
      timeline: "",
    },
  ]);
  const upd = (i, k, v) =>
    setQuotes(quotes.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)));
  const add = () =>
    setQuotes([
      ...quotes,
      {
        vendor: "",
        price: "",
        scope: "",
        material: "",
        warranty: "",
        timeline: "",
      },
    ]);
  const rem = (i) => setQuotes(quotes.filter((_, idx) => idx !== i));
  const save = async () => {
    await api.post("/tools/quote-comparisons", {
      title: title || "Quotation Comparison",
      quotes,
    });
    toast.success("Comparison saved");
  };

  return (
    <div className="bg-card border rounded-xl p-5">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-48">
          <Label className="mb-1.5 block text-sm">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Terrace Waterproofing"
            data-testid="qc-title"
          />
        </div>
        <Button
          variant="outline"
          onClick={add}
          className="gap-1.5 rounded-full"
          data-testid="qc-add"
        >
          <Plus className="w-4 h-4" /> Add Quote
        </Button>
        <Button
          onClick={save}
          className="gap-1.5 rounded-full"
          data-testid="qc-save"
        >
          <Save className="w-4 h-4" /> Save
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Factual comparison only — no winner is auto-declared.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              {[
                "Vendor",
                "Price",
                "Scope",
                "Material",
                "Warranty",
                "Timeline",
                "",
              ].map((h) => (
                <th key={h} className="py-2 pr-3 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {quotes.map((q, i) => (
              <tr key={i} className="border-b">
                {[
                  "vendor",
                  "price",
                  "scope",
                  "material",
                  "warranty",
                  "timeline",
                ].map((k) => (
                  <td key={k} className="py-1.5 pr-3">
                    <Input
                      className="h-9 min-w-28"
                      value={q[k]}
                      onChange={(e) => upd(i, k, e.target.value)}
                      data-testid={`qc-${k}-${i}`}
                    />
                  </td>
                ))}
                <td>
                  {quotes.length > 1 && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => rem(i)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
